// Renders children at document.body via React portal, positioned with
// fixed coordinates derived from an anchor element. Useful for dropdowns
// and popovers that would otherwise be clipped by ancestor scroll/overflow
// containers (notably the booking modal's overflow:auto wrapper).
//
// Width matches the anchor width by default. Position re-measures on
// window scroll + resize so the portal tracks the anchor when the user
// scrolls the underlying page or resizes the window.

import { useEffect, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';

interface AnchoredPortalProps {
  anchorRef: RefObject<HTMLElement | null>;
  isOpen: boolean;
  /** Vertical offset below the anchor, in px. Default 4. */
  offset?: number;
  /** Z-index for the portal layer. Default sits above the modal overlay. */
  zIndex?: number;
  /**
   * Placement relative to the anchor.
   *   - 'bottom': open below (default, legacy behavior)
   *   - 'top':    open above the anchor (the portal's bottom edge sits
   *               `offset` px above the anchor's top edge). The
   *               consumer is responsible for sizing the inner element
   *               to a useful height — the portal supplies `bottom`
   *               instead of `top` and lets the inner box's height
   *               drive layout.
   *   - 'auto':   pick top vs bottom based on which side has more
   *               vertical room.
   */
  placement?: 'top' | 'bottom' | 'auto';
  children: ReactNode;
}

interface Position {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
}

export function AnchoredPortal({
  anchorRef,
  isOpen,
  offset = 4,
  zIndex = 2147483641,
  placement = 'bottom',
  children,
}: AnchoredPortalProps) {
  const [pos, setPos] = useState<Position | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setPos(null);
      return;
    }
    const measure = () => {
      const el = anchorRef.current;
      if (!el) return;
      const vv = typeof window !== 'undefined' ? window.visualViewport : null;
      const isMobile = window.innerWidth < 1024;

      // Mobile + iOS keyboard: position:fixed refuses to follow the visual
      // viewport on iOS Safari — the dropdown floats at the input's
      // pre-keyboard layout position no matter how we measure. Pin to
      // the bottom of the VISUAL viewport instead (= just above the
      // keyboard), full-width, and let the input stay where the browser
      // put it. The customer's eye-line at booking time is the keyboard,
      // not the input, so this is also the most accessible layout.
      if (isMobile && vv && placement === 'top') {
        const layoutH = window.innerHeight;
        const keyboardOffset = Math.max(0, layoutH - (vv.offsetTop + vv.height));
        setPos({
          bottom: keyboardOffset + offset,
          left: 12,
          width: window.innerWidth - 24,
        });
        return;
      }

      // Desktop / non-keyboard: original anchor-following behavior.
      const r = el.getBoundingClientRect();
      const vh = vv ? vv.height : window.innerHeight;
      const vTop = vv ? vv.offsetTop : 0;
      const vLeft = vv ? vv.offsetLeft : 0;
      const spaceAbove = r.top;
      const spaceBelow = vh - r.bottom;
      const placeTop =
        placement === 'top' ||
        (placement === 'auto' && spaceAbove > spaceBelow);
      if (placeTop) {
        const layoutH = window.innerHeight;
        setPos({
          bottom: layoutH - (vTop + r.top) + offset,
          left: vLeft + r.left,
          width: r.width,
        });
      } else {
        setPos({
          top: vTop + r.bottom + offset,
          left: vLeft + r.left,
          width: r.width,
        });
      }
    };
    measure();

    // Re-measure on scroll/resize so the portal tracks the anchor. Use
    // capture on scroll so we catch scroll on any ancestor — including
    // the modal's overflow:auto wrapper — without needing to thread refs.
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    // VisualViewport fires resize/scroll independently when the soft
    // keyboard opens or the user pinch-zooms. Without these listeners the
    // portal stays glued to the input's pre-keyboard position on iOS.
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (vv) {
      vv.addEventListener('resize', measure);
      vv.addEventListener('scroll', measure);
    }
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
      if (vv) {
        vv.removeEventListener('resize', measure);
        vv.removeEventListener('scroll', measure);
      }
    };
  }, [anchorRef, isOpen, offset, placement]);

  if (!isOpen || !pos) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: pos.top,
        bottom: pos.bottom,
        left: pos.left,
        width: pos.width,
        zIndex,
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
